import { formatUkDateTime } from "./dateUtils";
import {
  auditStepLabel,
  auditStepRank,
  BOOKING_FUNNEL_STEPS,
  BOOKING_STEP_LABELS,
  normalizeAuditStepName,
  sessionReachedFunnelStep,
} from "./bookingFormAuditSteps";

const BOOKING_SOURCE_LABELS: Record<string, string> = {
  "southampton-port-taxi": "Southampton Port Taxi",
  "airport-transfers-taxi": "Airport Transfers Taxi",
  "uk-airport-taxi": "UK Airport Taxi",
  "liverpool-dock-taxi": "Liverpool Docks Taxi",
  "tikla-cars": "Tikla Cars",
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  form_started: "Form opened",
  step_viewed: "Step viewed",
  step_completed: "Step completed",
  field_updated: "Field updated",
  form_snapshot: "Form progress snapshot",
  validation_failed: "Validation failed",
  fare_quote_started: "Fare quote started",
  fare_quote_success: "Fare quote received",
  fare_quote_failed: "Fare quote failed",
  vehicle_selected: "Vehicle selected",
  confirm_started: "Confirm started",
  confirm_success: "Confirm success",
  confirm_failed: "Confirm failed",
  next_clicked: "Next clicked",
  back_clicked: "Back clicked",
  payment_step_entered: "Payment step",
  card_input_started: "Card input started",
  card_input_error: "Card input error",
  card_details_completed: "Card details completed",
  payment_intent_started: "Payment started",
  payment_intent_created: "Payment intent created",
  payment_succeeded: "Payment succeeded",
  payment_failed: "Payment failed",
  booking_created: "Booking created",
  booking_failed: "Booking save failed",
  page_viewed: "Page viewed",
  page_engagement: "Time on page",
  cta_clicked: "CTA clicked",
  nav_clicked: "Nav clicked",
  outbound_clicked: "Outbound link",
  call_clicked: "Call clicked",
  link_clicked: "Link clicked",
  language_popup_shown: "Language popup shown",
  language_selected: "Language selected",
  language_popup_dismissed: "Language popup dismissed",
};

export interface AuditEvent {
  id: string;
  sessionId: string;
  bookingSource: string;
  bookingDirectId: string | null;
  step: number | null;
  eventType: string;
  stepName: string | null;
  metadata: Record<string, unknown> | null;
  customerEmail: string | null;
  userAgent: string | null;
  ipAddress: string | null;
  deviceLabel?: string;
  createdAt: string;
  bookingDirect?: {
    id: string;
    bookingId: number;
    pasengerName: string | null;
    surName: string | null;
    email: string | null;
    cellno: string | null;
    status: string;
  } | null;
}

export interface AuditBookingContact {
  id: string;
  email: string | null;
  cellno: string | null;
  pasengerName: string | null;
  surName: string | null;
}

export interface VisitorSession {
  sessionId: string;
  consentGiven: boolean;
  deviceLabel?: string | null;
  browser?: string | null;
  os?: string | null;
  deviceType?: string | null;
  country?: string | null;
  region?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  geoSource?: string | null;
  isLikelyBot?: boolean | null;
  isLead?: boolean | null;
  language?: string | null;
  timezone?: string | null;
  ipAddress?: string | null;
  screenWidth?: number | null;
  screenHeight?: number | null;
  referrer?: string | null;
  landingPage?: string | null;
  trafficSource?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  gclid?: string | null;
  gadCampaignId?: string | null;
  engagementTimeSec?: number | null;
  maxScrollDepth?: number | null;
  pagesViewed?: number | null;
}

export interface AuditSession {
  sessionId: string;
  bookingSource: string;
  startedAt: string;
  lastEventAt: string;
  lastStep: number | null;
  lastStepName: string | null;
  customerEmail: string | null;
  bookingDirectId: string | null;
  bookingId: number | null;
  eventCount: number;
  completed: boolean;
  events: AuditEvent[];
  visitor: VisitorSession | null;
  bookingContact?: AuditBookingContact | null;
}

export type SessionSortKey =
  | "website"
  | "startedAt"
  | "lastEventAt"
  | "lastStep"
  | "events"
  | "customer"
  | "route"
  | "fare"
  | "location"
  | "device"
  | "status"
  | "landingPage"
  | "trafficSource"
  | "engagement";

export type SortDirection = "asc" | "desc";

export type SessionStatusFilter = "all" | "completed" | "abandoned";
export type SessionContactFilter = "all" | "has_email" | "has_phone" | "has_name";
export type SessionRouteFilter = "all" | "has_pickup" | "has_route";

export type SessionAudienceFilter = "leads" | "bots" | "all";

const UK_COUNTRY_ALIASES = new Set([
  "united kingdom",
  "uk",
  "gb",
  "great britain",
]);

/** Match stored geo labels for UK (United Kingdom / GB / UK). */
export function countryMatchesFilter(
  sessionCountry: string | null | undefined,
  filterCountry: string,
): boolean {
  const session = (sessionCountry ?? "").trim().toLowerCase();
  const filter = filterCountry.trim().toLowerCase();
  if (!filter) return true;
  if (!session) return false;
  if (UK_COUNTRY_ALIASES.has(filter)) {
    return UK_COUNTRY_ALIASES.has(session);
  }
  return session === filter;
}

export interface SessionClientFilters {
  audience: SessionAudienceFilter;
  status: SessionStatusFilter;
  lastStep: string;
  reachedStep: string;
  contact: SessionContactFilter;
  route: SessionRouteFilter;
  search: string;
  landingPage: string;
  trafficSource: string;
  deviceType: string;
  country: string;
  browser: string;
  hadValidationError: boolean;
  hadFareQuote: boolean;
  hadFareQuoteFailure: boolean;
  hadPaymentFailure: boolean;
  hadBookingFailure: boolean;
  reachedPayment: boolean;
  cookieConsent: boolean;
  cardEntry: "" | "completed" | "not_completed" | "error";
}

export const DEFAULT_SESSION_FILTERS: SessionClientFilters = {
  audience: "leads",
  status: "all",
  lastStep: "",
  reachedStep: "",
  contact: "all",
  route: "all",
  search: "",
  landingPage: "",
  trafficSource: "",
  deviceType: "",
  country: "",
  browser: "",
  hadValidationError: false,
  hadFareQuote: false,
  hadFareQuoteFailure: false,
  hadPaymentFailure: false,
  hadBookingFailure: false,
  reachedPayment: false,
  cookieConsent: false,
  cardEntry: "",
};

/** Show bots/all traffic with no country lock. */
export const ALL_TRAFFIC_FILTERS: SessionClientFilters = {
  ...DEFAULT_SESSION_FILTERS,
  audience: "all",
  country: "",
};

export type AuditStatFilterPreset =
  | "all"
  | "completed"
  | "abandoned"
  | "cookieConsent"
  | "paymentFailure"
  | "validationFailure"
  | "fareQuoteFailure";

export function applyAuditStatFilterPreset(
  preset: AuditStatFilterPreset,
): SessionClientFilters {
  const base = { ...DEFAULT_SESSION_FILTERS };
  switch (preset) {
    case "all":
      return base;
    case "completed":
      return { ...base, status: "completed" };
    case "abandoned":
      return { ...base, status: "abandoned" };
    case "cookieConsent":
      return { ...base, cookieConsent: true };
    case "paymentFailure":
      return { ...base, hadPaymentFailure: true };
    case "validationFailure":
      return { ...base, hadValidationError: true };
    case "fareQuoteFailure":
      return { ...base, hadFareQuoteFailure: true };
  }
}

export function applyAbandonedAtStepFilter(stepName: string): SessionClientFilters {
  return {
    ...DEFAULT_SESSION_FILTERS,
    status: "abandoned",
    lastStep: stepName,
  };
}

export function applyReachedStepFilter(stepName: string): SessionClientFilters {
  return {
    ...DEFAULT_SESSION_FILTERS,
    reachedStep: stepName,
  };
}

export function applyDeviceFilter(deviceType: string): SessionClientFilters {
  return {
    ...DEFAULT_SESSION_FILTERS,
    deviceType,
    cookieConsent: true,
  };
}

export function applyCountryFilter(country: string): SessionClientFilters {
  return {
    ...DEFAULT_SESSION_FILTERS,
    country,
  };
}

export function applyBrowserFilter(browser: string): SessionClientFilters {
  return {
    ...DEFAULT_SESSION_FILTERS,
    browser,
    cookieConsent: true,
  };
}

export function applyTrafficSourceFilter(trafficSource: string): SessionClientFilters {
  return {
    ...DEFAULT_SESSION_FILTERS,
    trafficSource,
  };
}

export function applyLandingPageFilter(path: string): SessionClientFilters {
  return {
    ...DEFAULT_SESSION_FILTERS,
    landingPage: path,
  };
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const PROGRESS_FIELD_LABELS: Record<string, string> = {
  pickup: "Pickup",
  dropoff: "Drop-off",
  pickupQuery: "Pickup (typed)",
  dropoffQuery: "Drop-off (typed)",
  journeyType: "Journey",
  returnPickup: "Return pickup",
  returnDropoff: "Return drop-off",
  returnMirrorDropoff: "Return drop-off same as outbound pickup",
  returnDate: "Return date",
  returnTime: "Return time",
  pickupDate: "Date",
  pickupTime: "Time",
  flightTracking: "Flight tracking",
  airportPickupType: "Airport pickup",
  flightNo: "Flight no.",
  arrivingFrom: "Arriving from",
  terminal: "Terminal",
  selectedVehicleId: "Vehicle",
  selectedVehicleName: "Vehicle",
  fare: "Fare (£)",
  estimatedFareMin: "Est. fare min (£)",
  estimatedFareMax: "Est. fare max (£)",
  estimatedFareLabel: "Est. fare",
  adults: "Adults",
  children: "Children",
  luggage: "Luggage",
  childSeat: "Child seat",
  wheelchair: "Wheelchair access",
  assistanceDog: "Assistance dog",
  notes: "Notes",
  paymentMethod: "Payment",
  firstName: "First name",
  surname: "Surname",
  mobileCountryCode: "Country code",
  mobile: "Mobile",
  email: "Email",
  termsAccepted: "Terms accepted",
  viaCount: "Via stops",
  viaAction: "Via action",
  via: "Via stop",
  couponCode: "Coupon",
  couponAmount: "Coupon amount (£)",
};

const DISPLAY_ORDER = [
  "pickup",
  "dropoff",
  "viaCount",
  "journeyType",
  "returnPickup",
  "returnDropoff",
  "returnMirrorDropoff",
  "pickupDate",
  "pickupTime",
  "returnDate",
  "returnTime",
  "flightNo",
  "terminal",
  "arrivingFrom",
  "adults",
  "children",
  "luggage",
  "estimatedFareLabel",
  "fare",
  "couponCode",
  "couponAmount",
  "firstName",
  "surname",
  "email",
  "mobileCountryCode",
  "mobile",
  "paymentMethod",
  "childSeat",
  "wheelchair",
  "assistanceDog",
  "notes",
  "termsAccepted",
];

function isMeaningful(value: unknown, key?: string): boolean {
  if (value == null || value === "") return false;
  if (key === "fare" && typeof value === "number" && value <= 0) return false;
  if (typeof value === "boolean") {
    if (key === "returnMirrorDropoff" || key === "termsAccepted") return true;
    return value;
  }
  if (typeof value === "number" && !Number.isNaN(value)) return true;
  return String(value).trim().length > 0;
}

function isVehicleId(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function vehicleNameFromEvents(events: AuditEvent[]): string | undefined {
  let name: string | undefined;
  for (const event of events) {
    if (!event.metadata) continue;

    const vehicleName = event.metadata.vehicleName ?? event.metadata.selectedVehicleName;
    if (typeof vehicleName === "string" && vehicleName.trim()) {
      name = vehicleName.trim();
      continue;
    }

    if (
      (event.eventType === "vehicle_selected" ||
        event.eventType === "fare_quote_success" ||
        event.eventType === "step_completed") &&
      typeof event.metadata.selectedVehicleName === "string" &&
      event.metadata.selectedVehicleName.trim()
    ) {
      name = event.metadata.selectedVehicleName.trim();
    }
  }
  return name;
}

export function buildVehicleNameLookup(
  vehicleTypes: {
    id: string;
    name?: string | null;
    description?: string | null;
  }[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const vehicleType of vehicleTypes) {
    const name = vehicleType.name?.trim();
    const description = vehicleType.description?.trim();
    const label = name || description;
    if (label) map[vehicleType.id] = label;
  }
  return map;
}

export function resolveVehicleDisplayName(
  progress: Record<string, unknown>,
  events: AuditEvent[] = [],
  vehicleNames?: Record<string, string>,
): string | undefined {
  const explicit = progress.selectedVehicleName;
  if (typeof explicit === "string") {
    const trimmed = explicit.trim();
    if (trimmed && !isVehicleId(trimmed)) return trimmed;
  }

  const fromEvent = vehicleNameFromEvents(events);
  if (fromEvent) return fromEvent;

  const vehicleId = progress.selectedVehicleId;
  if (typeof vehicleId === "string" && vehicleNames?.[vehicleId]) {
    return vehicleNames[vehicleId];
  }

  return undefined;
}

function formatFieldValue(key: string, value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (key === "fare") {
    const fare = Number(value);
    if (Number.isFinite(fare) && fare > 0) return `£${fare.toFixed(2)}`;
    return "";
  }
  if (key === "estimatedFareLabel" && typeof value === "string") return value;
  if (key === "estimatedFareMin" || key === "estimatedFareMax") {
    const fare = Number(value);
    if (Number.isFinite(fare) && fare > 0) return `£${fare}`;
  }
  if (key === "journeyType" && typeof value === "string") {
    return value.replace(/_/g, " ");
  }
  if (key === "couponAmount") {
    const amount = Number(value);
    if (Number.isFinite(amount)) return `£${amount.toFixed(2)}`;
  }
  if (key === "viaAction" && typeof value === "string") {
    return value.replace(/_/g, " ");
  }
  return String(value);
}

/** Merge field updates + snapshots in event order into one customer progress object. */
export function extractCustomerProgress(
  events: AuditEvent[],
): Record<string, unknown> {
  const progress: Record<string, unknown> = {};

  // Process chronologically so a later clear is not undone by an earlier snapshot.
  for (const event of events) {
    if (!event.metadata) continue;

    if (event.eventType === "field_updated") {
      const field = event.metadata.field;
      const value = event.metadata.value;
      if (typeof field !== "string") continue;
      if (event.metadata.cleared || value == null || value === "") {
        delete progress[field];
        continue;
      }
      if (typeof value === "boolean" || isMeaningful(value, field)) {
        progress[field] = value;
      }
      continue;
    }

    if (
      event.eventType === "step_completed" ||
      event.eventType === "fare_quote_success" ||
      event.eventType === "vehicle_selected" ||
      event.eventType === "next_clicked"
    ) {
      for (const key of [
        "pickup",
        "dropoff",
        "returnPickup",
        "returnDropoff",
        "fare",
        "selectedVehicleName",
        "selectedVehicleId",
        "journeyType",
        "pickupDate",
        "pickupTime",
      ]) {
        const value = event.metadata[key];
        if (isMeaningful(value, key)) progress[key] = value;
      }
    }

    if (event.eventType !== "form_snapshot") continue;

    const snapshotHadCoupon = Object.prototype.hasOwnProperty.call(
      event.metadata,
      "couponCode",
    );
    for (const [key, value] of Object.entries(event.metadata)) {
      if (key === "fieldsFilled") continue;
      if (typeof value === "boolean" && (key === "returnMirrorDropoff" || key === "termsAccepted")) {
        progress[key] = value;
        continue;
      }
      if (isMeaningful(value, key)) progress[key] = value;
    }
    // Snapshot without coupon means it was not applied at that moment.
    if (!snapshotHadCoupon || !isMeaningful(event.metadata.couponCode, "couponCode")) {
      delete progress.couponCode;
      delete progress.couponAmount;
    }
  }

  const vehicleName = vehicleNameFromEvents(events);
  if (vehicleName && !isMeaningful(progress.selectedVehicleName)) {
    progress.selectedVehicleName = vehicleName;
  }

  // Once a real vehicle fare is known, hide the early estimate (often inflated / stale).
  if (Number(progress.fare) > 0) {
    delete progress.estimatedFareLabel;
    delete progress.estimatedFareMin;
    delete progress.estimatedFareMax;
  }

  return progress;
}

function isMaskedAuditValue(key: string, value: unknown): boolean {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  const lower = key.toLowerCase();
  if (lower.includes("email")) {
    return trimmed === "***" || /^\S\*\*\*@/.test(trimmed);
  }
  if (lower.includes("mobile") || lower.includes("phone") || lower.includes("cell")) {
    const digits = trimmed.replace(/\D/g, "");
    return trimmed.startsWith("***") && digits.length <= 4;
  }
  if (
    lower === "firstname" ||
    lower === "surname" ||
    lower === "lastname" ||
    lower === "passengername"
  ) {
    return /^\S\*\*\*$/.test(trimmed);
  }
  return false;
}

/** Prefer full contact details from session/booking over masked snapshot values. */
export function enrichCustomerProgress(
  progress: Record<string, unknown>,
  session: Pick<AuditSession, "customerEmail" | "bookingContact">,
): Record<string, unknown> {
  const enriched = { ...progress };
  const booking = session.bookingContact;

  const email = session.customerEmail ?? booking?.email ?? null;
  if (
    email &&
    (!isMeaningful(enriched.email) || isMaskedAuditValue("email", enriched.email))
  ) {
    enriched.email = email;
  }

  if (
    booking?.pasengerName &&
    (!isMeaningful(enriched.firstName) ||
      isMaskedAuditValue("firstName", enriched.firstName))
  ) {
    enriched.firstName = booking.pasengerName;
  }

  if (
    booking?.surName &&
    (!isMeaningful(enriched.surname) ||
      isMaskedAuditValue("surname", enriched.surname))
  ) {
    enriched.surname = booking.surName;
  }

  if (
    booking?.cellno &&
    (!isMeaningful(enriched.mobile) ||
      isMaskedAuditValue("mobile", enriched.mobile))
  ) {
    enriched.mobile = booking.cellno;
  }

  return enriched;
}

export function getSessionCustomerProgress(session: AuditSession): Record<string, unknown> {
  return enrichCustomerProgress(extractCustomerProgress(session.events), session);
}

export function getCustomerDisplayName(progress: Record<string, unknown>): string {
  const first = String(progress.firstName ?? "").trim();
  const last = String(progress.surname ?? "").trim();
  const combined = [first, last].filter(Boolean).join(" ");
  return combined || "—";
}

export function getRouteSummary(progress: Record<string, unknown>): string {
  const pickup = String(progress.pickup ?? progress.pickupQuery ?? "").trim();
  const dropoff = String(progress.dropoff ?? progress.dropoffQuery ?? "").trim();
  const returnPickup = String(progress.returnPickup ?? "").trim();
  const returnDropoff = String(progress.returnDropoff ?? "").trim();
  const outbound =
    pickup && dropoff ? `${pickup} → ${dropoff}` : pickup || dropoff || "";
  if (returnPickup || returnDropoff) {
    const ret =
      returnPickup && returnDropoff
        ? `${returnPickup} → ${returnDropoff}`
        : returnPickup || returnDropoff;
    if (outbound) return `${outbound} · Return: ${ret}`;
    return `Return: ${ret}`;
  }
  if (outbound) return outbound;
  return "—";
}

export interface ProgressDisplayOptions {
  events?: AuditEvent[];
  vehicleNames?: Record<string, string>;
}

export function getFormattedProgressEntries(
  progress: Record<string, unknown>,
  options?: ProgressDisplayOptions,
): { key: string; label: string; value: string }[] {
  const events = options?.events ?? [];
  const vehicleNames = options?.vehicleNames;
  const resolvedVehicleName = resolveVehicleDisplayName(
    progress,
    events,
    vehicleNames,
  );

  const keys = new Set([
    ...DISPLAY_ORDER,
    ...Object.keys(progress).filter((k) => k !== "fieldsFilled"),
  ]);

  return Array.from(keys)
    .filter((key) => key !== "selectedVehicleName")
    .filter((key) => key !== "estimatedFareMin" && key !== "estimatedFareMax")
    .filter((key) => key !== "viaAction")
    .filter((key) => isMeaningful(progress[key], key))
    .map((key) => {
      if (key === "selectedVehicleId" && resolvedVehicleName) {
        return {
          key,
          label: PROGRESS_FIELD_LABELS[key] ?? key.replace(/_/g, " "),
          value: resolvedVehicleName,
        };
      }

      const rawValue = progress[key];
      if (key === "selectedVehicleId" && isVehicleId(rawValue)) {
        return {
          key,
          label: PROGRESS_FIELD_LABELS[key] ?? key.replace(/_/g, " "),
          value: "Unknown vehicle",
        };
      }

      return {
        key,
        label: PROGRESS_FIELD_LABELS[key] ?? key.replace(/_/g, " "),
        value: formatFieldValue(key, rawValue),
      };
    });
}

export function sessionHasEvent(session: AuditSession, eventType: string): boolean {
  return session.events.some((e) => e.eventType === eventType);
}

export type CardEntryStatus =
  | "completed"
  | "error"
  | "started"
  | "not_started"
  | "not_reached";

export const CARD_ENTRY_STATUS_LABELS: Record<CardEntryStatus, string> = {
  completed: "Card entered",
  error: "Card input error",
  started: "Card started, not completed",
  not_started: "No card details",
  not_reached: "—",
};

/**
 * Whether the customer typed card details at the payment step, so payment-step
 * abandonment can be separated from card-entry issues. A successful payment
 * implies the card was entered (covers sessions recorded before card tracking).
 */
export function getCardEntryStatus(session: AuditSession): CardEntryStatus {
  if (
    sessionHasEvent(session, "card_details_completed") ||
    sessionHasEvent(session, "payment_succeeded")
  ) {
    return "completed";
  }
  if (sessionHasEvent(session, "card_input_error")) return "error";
  if (sessionHasEvent(session, "card_input_started")) return "started";
  const reachedPayment =
    sessionHasEvent(session, "payment_step_entered") ||
    sessionHasEvent(session, "payment_intent_started") ||
    stepRank(session.lastStepName) >= stepRank("payment");
  return reachedPayment ? "not_started" : "not_reached";
}

export function sessionReachedStep(session: AuditSession, stepName: string): boolean {
  return sessionReachedFunnelStep(session.events, stepName as "where" | "when" | "vehicle" | "payment");
}

export function formatLocation(visitor: VisitorSession | null): string {
  if (!visitor) return "—";
  const parts = [visitor.city, visitor.region, visitor.country].filter(Boolean);
  if (parts.length > 0) return parts.join(", ");
  const latitude = Number(visitor.latitude);
  const longitude = Number(visitor.longitude);
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  }
  if (visitor.landingPage) return "Landing only";
  return visitor.consentGiven ? "—" : "No consent";
}

export function formatDevice(visitor: VisitorSession | null): string {
  if (!visitor) return "—";
  const parts = [
    visitor.deviceType,
    visitor.browser,
    visitor.os,
    visitor.deviceLabel,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

export function formatLandingPage(visitor: VisitorSession | null): string {
  if (!visitor?.landingPage) return "—";
  try {
    const url = new URL(visitor.landingPage);
    return `${url.pathname}${url.search}`;
  } catch {
    return visitor.landingPage;
  }
}

export function trafficSourceLabel(source: string | null | undefined): string {
  switch (source) {
    case "google_organic":
      return "Google Organic";
    case "google_ads":
      return "Google Ads";
    case "bing_organic":
      return "Bing Organic";
    case "bing_ads":
      return "Bing Ads";
    case "social":
      return "Social";
    case "ai_referral":
      return "AI / ChatGPT";
    case "email":
      return "Email";
    case "referral":
      return "Referral";
    case "direct":
      return "Direct";
    default:
      return source ? source.replace(/_/g, " ") : "—";
  }
}

export function formatTrafficSource(visitor: VisitorSession | null): string {
  if (!visitor?.trafficSource) return "—";
  return trafficSourceLabel(visitor.trafficSource);
}

export function formatEngagement(visitor: VisitorSession | null): string {
  if (!visitor) return "—";
  const parts: string[] = [];
  if (visitor.engagementTimeSec && visitor.engagementTimeSec > 0) {
    const mins = Math.floor(visitor.engagementTimeSec / 60);
    const secs = visitor.engagementTimeSec % 60;
    parts.push(mins > 0 ? `${mins}m ${secs}s` : `${secs}s`);
  }
  if (visitor.maxScrollDepth != null && visitor.maxScrollDepth > 0) {
    parts.push(`${visitor.maxScrollDepth}% scroll`);
  }
  if (visitor.pagesViewed && visitor.pagesViewed > 0) {
    parts.push(`${visitor.pagesViewed} page${visitor.pagesViewed === 1 ? "" : "s"}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "—";
}

function stepRank(stepName: string | null): number {
  return auditStepRank(stepName);
}

export { BOOKING_FUNNEL_STEPS, BOOKING_STEP_LABELS, auditStepLabel };

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function compareNumbers(a: number, b: number): number {
  return a - b;
}

export function sortSessions(
  sessions: AuditSession[],
  sortKey: SessionSortKey,
  direction: SortDirection,
): AuditSession[] {
  const directionFactor = direction === "asc" ? 1 : -1;

  return [...sessions].sort((a, b) => {
    const progressA = getSessionCustomerProgress(a);
    const progressB = getSessionCustomerProgress(b);

    let result = 0;
    switch (sortKey) {
      case "website":
        result = compareStrings(a.bookingSource, b.bookingSource);
        break;
      case "startedAt":
        result = compareStrings(a.startedAt, b.startedAt);
        break;
      case "lastEventAt":
        result = compareStrings(a.lastEventAt, b.lastEventAt);
        break;
      case "lastStep":
        result = compareNumbers(
          stepRank(a.lastStepName),
          stepRank(b.lastStepName),
        );
        break;
      case "events":
        result = compareNumbers(a.eventCount, b.eventCount);
        break;
      case "customer": {
        const nameA =
          a.customerEmail ||
          String(progressA.email ?? "") ||
          getCustomerDisplayName(progressA);
        const nameB =
          b.customerEmail ||
          String(progressB.email ?? "") ||
          getCustomerDisplayName(progressB);
        result = compareStrings(nameA, nameB);
        break;
      }
      case "route":
        result = compareStrings(
          getRouteSummary(progressA),
          getRouteSummary(progressB),
        );
        break;
      case "fare": {
        const fareA = Number(progressA.fare ?? 0);
        const fareB = Number(progressB.fare ?? 0);
        result = compareNumbers(fareA, fareB);
        break;
      }
      case "location":
        result = compareStrings(
          formatLocation(a.visitor),
          formatLocation(b.visitor),
        );
        break;
      case "device":
        result = compareStrings(
          formatDevice(a.visitor),
          formatDevice(b.visitor),
        );
        break;
      case "status":
        result = compareNumbers(
          a.completed ? 1 : 0,
          b.completed ? 1 : 0,
        );
        break;
      case "landingPage":
        result = compareStrings(
          formatLandingPage(a.visitor),
          formatLandingPage(b.visitor),
        );
        break;
      case "trafficSource":
        result = compareStrings(
          formatTrafficSource(a.visitor),
          formatTrafficSource(b.visitor),
        );
        break;
      case "engagement":
        result = compareNumbers(
          a.visitor?.engagementTimeSec ?? 0,
          b.visitor?.engagementTimeSec ?? 0,
        );
        break;
      default:
        result = 0;
    }

    if (result === 0) {
      // Stable secondary key: newest activity first regardless of column sort.
      result = compareStrings(b.lastEventAt, a.lastEventAt);
      return result;
    }

    return result * directionFactor;
  });
}

export function filterSessions(
  sessions: AuditSession[],
  filters: SessionClientFilters,
): AuditSession[] {
  const search = filters.search.trim().toLowerCase();
  const landing = filters.landingPage.trim().toLowerCase();

  return sessions.filter((session) => {
    const progress = getSessionCustomerProgress(session);

    if (filters.status === "completed" && !session.completed) return false;
    if (filters.status === "abandoned" && session.completed) return false;

    if (filters.lastStep && normalizeAuditStepName(session.lastStepName) !== filters.lastStep) {
      return false;
    }

    if (filters.reachedStep && !sessionReachedStep(session, filters.reachedStep)) {
      return false;
    }

    if (filters.cookieConsent && !session.visitor?.consentGiven) {
      return false;
    }

    if (
      filters.deviceType &&
      (session.visitor?.deviceType ?? "").toLowerCase() !==
        filters.deviceType.toLowerCase()
    ) {
      return false;
    }

    if (
      filters.country &&
      !countryMatchesFilter(session.visitor?.country, filters.country)
    ) {
      return false;
    }

    if (
      filters.browser &&
      (session.visitor?.browser ?? "").toLowerCase() !== filters.browser.toLowerCase()
    ) {
      return false;
    }

    const email =
      session.customerEmail ?? String(progress.email ?? "").trim();
    const phone = String(progress.mobile ?? "").trim();
    const name = getCustomerDisplayName(progress);

    if (filters.contact === "has_email" && !email) return false;
    if (filters.contact === "has_phone" && !phone) return false;
    if (filters.contact === "has_name" && name === "—") return false;

    const pickup = String(progress.pickup ?? "").trim();
    const dropoff = String(progress.dropoff ?? "").trim();
    if (filters.route === "has_pickup" && !pickup) return false;
    if (filters.route === "has_route" && (!pickup || !dropoff)) return false;

    if (filters.hadValidationError && !sessionHasEvent(session, "validation_failed")) {
      return false;
    }
    if (filters.hadFareQuoteFailure && !sessionHasEvent(session, "fare_quote_failed")) {
      return false;
    }
    if (filters.hadPaymentFailure && !sessionHasEvent(session, "payment_failed")) {
      return false;
    }
    if (filters.hadBookingFailure && !sessionHasEvent(session, "booking_failed")) {
      return false;
    }
    if (filters.cardEntry === "completed" && getCardEntryStatus(session) !== "completed") {
      return false;
    }
    if (filters.cardEntry === "error" && !sessionHasEvent(session, "card_input_error")) {
      return false;
    }
    if (filters.cardEntry === "not_completed") {
      const cardStatus = getCardEntryStatus(session);
      if (cardStatus === "completed" || cardStatus === "not_reached") return false;
    }
    if (
      filters.hadFareQuote &&
      !sessionHasEvent(session, "fare_quote_success") &&
      !sessionHasEvent(session, "fare_quote_started")
    ) {
      return false;
    }
    if (
      filters.reachedPayment &&
      !sessionHasEvent(session, "payment_step_entered") &&
      stepRank(session.lastStepName) < stepRank("payment")
    ) {
      return false;
    }

    if (landing) {
      const page = formatLandingPage(session.visitor).toLowerCase();
      if (!page.includes(landing)) return false;
    }

    if (
      filters.trafficSource &&
      (session.visitor?.trafficSource ?? "") !== filters.trafficSource
    ) {
      return false;
    }

    if (search) {
      const haystack = [
        session.sessionId,
        session.customerEmail,
        email,
        phone,
        name,
        pickup,
        dropoff,
        getRouteSummary(progress),
        formatLocation(session.visitor),
        formatLandingPage(session.visitor),
        formatTrafficSource(session.visitor),
        session.visitor?.utmTerm ?? "",
        session.visitor?.utmCampaign ?? "",
        session.visitor?.utmSource ?? "",
        session.visitor?.referrer ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }

    return true;
  });
}

export interface PaginatedResult<T> {
  items: T[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  rangeStart: number;
  rangeEnd: number;
}

export function paginateItems<T>(
  items: T[],
  currentPage: number,
  pageSize: number,
): PaginatedResult<T> {
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  return {
    items: items.slice(startIndex, endIndex),
    totalItems,
    totalPages,
    currentPage: safePage,
    pageSize,
    rangeStart: totalItems === 0 ? 0 : startIndex + 1,
    rangeEnd: endIndex,
  };
}

export function buildPageNumbers(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>([1, totalPages, currentPage]);
  if (currentPage > 1) pages.add(currentPage - 1);
  if (currentPage < totalPages) pages.add(currentPage + 1);
  if (currentPage > 2) pages.add(currentPage - 2);
  if (currentPage < totalPages - 1) pages.add(currentPage + 2);

  return Array.from(pages).sort((a, b) => a - b);
}

function formatAuditDateTime(value: string): string {
  try {
    return formatUkDateTime(value);
  } catch {
    return value;
  }
}

function formatEventTypeLabel(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType] ?? eventType.replace(/_/g, " ");
}

function formatEventMetadataPreview(
  metadata: Record<string, unknown> | null,
  eventType?: string,
): string {
  if (!metadata || Object.keys(metadata).length === 0) return "";
  if (eventType === "page_engagement") {
    const parts: string[] = [];
    if (metadata.pagePath) parts.push(`Page: ${String(metadata.pagePath)}`);
    if (metadata.path) parts.push(`Page: ${String(metadata.path)}`);
    if (metadata.activeSec != null) parts.push(`Active: ${String(metadata.activeSec)}s`);
    if (metadata.scrollDepth != null) parts.push(`Scroll: ${String(metadata.scrollDepth)}%`);
    if (metadata.scrollDepthMax != null) {
      parts.push(`Scroll: ${String(metadata.scrollDepthMax)}%`);
    }
    if (metadata.pageExit != null) parts.push(metadata.pageExit ? "Exit" : "Heartbeat");
    return parts.join(" · ");
  }
  if (
    eventType === "cta_clicked" ||
    eventType === "nav_clicked" ||
    eventType === "outbound_clicked" ||
    eventType === "call_clicked" ||
    eventType === "link_clicked"
  ) {
    const parts: string[] = [];
    if (metadata.label) parts.push(String(metadata.label));
    if (metadata.placement) parts.push(`Placement: ${String(metadata.placement)}`);
    if (metadata.section) parts.push(`Section: ${String(metadata.section)}`);
    if (metadata.href) parts.push(`→ ${String(metadata.href)}`);
    if (metadata.path) parts.push(`On: ${String(metadata.path)}`);
    return parts.filter(Boolean).join(" · ");
  }
  if (eventType === "page_viewed") {
    const parts: string[] = [];
    if (metadata.path) parts.push(String(metadata.path));
    if (metadata.referrer) parts.push(`From: ${String(metadata.referrer)}`);
    return parts.join(" · ");
  }
  if (
    eventType === "language_popup_shown" ||
    eventType === "language_selected" ||
    eventType === "language_popup_dismissed"
  ) {
    const parts: string[] = [];
    if (metadata.fromLocale) parts.push(`From: ${String(metadata.fromLocale)}`);
    if (metadata.toLocale) parts.push(`To: ${String(metadata.toLocale)}`);
    if (metadata.suggestedLocale) parts.push(`Suggested: ${String(metadata.suggestedLocale)}`);
    if (metadata.via) parts.push(`Via: ${String(metadata.via)}`);
    if (metadata.path) parts.push(`On: ${String(metadata.path)}`);
    return parts.join(" · ");
  }
  if (eventType === "field_updated" && typeof metadata.field === "string") {
    const label = PROGRESS_FIELD_LABELS[metadata.field] ?? metadata.field;
    if (metadata.cleared) return `${label}: (cleared)`;
    const value =
      typeof metadata.value === "boolean"
        ? metadata.value
          ? "Yes"
          : "No"
        : String(metadata.value ?? "");
    return `${label}: ${value}`;
  }
  return Object.entries(metadata)
    .slice(0, 6)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(" · ");
}

/**
 * Plain-text reconstruction of a visitor session for clipboard / AI analysis.
 */
export function buildAuditJourneyText(
  session: AuditSession,
  options?: { vehicleNames?: Record<string, string> },
): string {
  const progress = getSessionCustomerProgress(session);
  const progressEntries = getFormattedProgressEntries(progress, {
    events: session.events,
    vehicleNames: options?.vehicleNames,
  });
  const website =
    BOOKING_SOURCE_LABELS[session.bookingSource] ?? session.bookingSource;
  const cardStatus = getCardEntryStatus(session);
  const lines: string[] = [
    "Booking form audit journey",
    "==========================",
    "",
    `Session ID: ${session.sessionId}`,
    `Website: ${website}`,
    `Status: ${session.completed ? "Completed" : "Abandoned"}`,
    `Started: ${formatAuditDateTime(session.startedAt)}`,
    `Last activity: ${formatAuditDateTime(session.lastEventAt)}`,
    `Events: ${session.eventCount}`,
    `Last step: ${session.lastStepName ? auditStepLabel(session.lastStepName) : "—"}`,
  ];

  if (cardStatus !== "not_reached") {
    lines.push(`Card details: ${CARD_ENTRY_STATUS_LABELS[cardStatus]}`);
  }

  if (session.bookingId != null || session.bookingDirectId) {
    lines.push(
      `Booking: #${session.bookingId ?? session.bookingDirectId ?? "—"}`,
    );
  } else {
    lines.push("Booking: none");
  }

  lines.push("", "Customer entered", "----------------");
  if (progressEntries.length === 0) {
    lines.push("(no form fields captured)");
  } else {
    for (const entry of progressEntries) {
      lines.push(`${entry.label}: ${entry.value}`);
    }
  }

  const route = getRouteSummary(progress);
  if (route !== "—") {
    lines.push(`Route summary: ${route}`);
  }

  lines.push("", "Visitor context", "---------------");
  if (!session.visitor) {
    lines.push("(no visitor context)");
  } else {
    const v = session.visitor;
    lines.push(`Location: ${formatLocation(v)}`);
    lines.push(`Device: ${formatDevice(v)}`);
    lines.push(`IP: ${v.ipAddress ?? "—"}`);
    lines.push(`Language: ${v.language ?? "—"}`);
    lines.push(`Timezone: ${v.timezone ?? "—"}`);
    lines.push(
      `Screen: ${
        v.screenWidth && v.screenHeight
          ? `${v.screenWidth}×${v.screenHeight}`
          : "—"
      }`,
    );
    lines.push(`Geo source: ${v.geoSource ?? "—"}`);
    lines.push(`Traffic: ${formatTrafficSource(v)}`);
    lines.push(`Referrer: ${v.referrer || "Direct"}`);
    if (v.utmSource) lines.push(`UTM source: ${v.utmSource}`);
    if (v.utmMedium) lines.push(`UTM medium: ${v.utmMedium}`);
    if (v.utmCampaign) lines.push(`UTM campaign: ${v.utmCampaign}`);
    if (v.utmTerm) lines.push(`UTM term: ${v.utmTerm}`);
    if (v.gclid) lines.push(`gclid: ${v.gclid}`);
    lines.push(`Engagement: ${formatEngagement(v)}`);
    lines.push(`Landing: ${v.landingPage || "—"}`);
    lines.push(`Cookie consent: ${v.consentGiven ? "yes" : "no"}`);
  }

  lines.push("", "Event timeline", "--------------");
  if (session.events.length === 0) {
    lines.push("(no events)");
  } else {
    session.events.forEach((event, index) => {
      const parts = [
        `${index + 1}. [${formatAuditDateTime(event.createdAt)}]`,
        formatEventTypeLabel(event.eventType),
      ];
      if (event.stepName) parts.push(`(${auditStepLabel(event.stepName)})`);
      const meta = formatEventMetadataPreview(event.metadata, event.eventType);
      let line = parts.join(" ");
      if (meta) line += ` — ${meta}`;
      lines.push(line);
    });
  }

  lines.push("");
  return lines.join("\n");
}
